import { makeAutoObservable } from 'mobx';
import { SportsbookSelectionsState } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/sportsbook-sections/SportsbookSelections.state';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { CasinoSelectionsState } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/casino-sections/CasinoSections.state';
import {
    checkIsArray,
    checkIsNumber,
    checkString,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { deserializeNodeValue } from './sportsbook-sections/sports-picker/utils';
import { Common } from 'src/AppState/Common';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import {
    ActionConditionsType,
    CasinoCriteriaKeyType,
    CasinoCriteriaType,
    SportsbookCriteriaKeyType,
    SportsbookCriteriaType,
    CasinoCriteriaItemType,
    SportsbookCriteriaItemType,
} from './ActionConditions.types';
import { GeneralDetailsState } from 'src/domains/casino/webview/bonus-engine/campaign/general-details/GeneralDetails.state';

export type ActionConditionsFormType = {
    bettingPlatformForm: string;
};

export interface BetResultType {
    id: string;
    value: 'win' | 'loss';
    label: string;
}
export const allResultsOptions: Array<BetResultType> = [
    { id: 'win', value: 'win', label: 'Win' },
    { id: 'loss', value: 'loss', label: 'Loss' },
];

export class ActionConditionsState {
    public readonly bettingPlatformForm: FormInputState<string, string>;

    public readonly sportsbookSelectionsState: SportsbookSelectionsState;
    public readonly casinoSelectionsState: CasinoSelectionsState;
    private readonly form: FormModel<ActionConditionsFormType>;

    public constructor(
        private readonly common: Common,
        private readonly campaignId: string | undefined,
        private readonly generalDetailsState: GeneralDetailsState
    ) {
        makeAutoObservable(this);

        this.sportsbookSelectionsState = new SportsbookSelectionsState(this.common, this.campaignId);
        this.casinoSelectionsState = new CasinoSelectionsState(this.common, this.campaignId);

        this.bettingPlatformForm = FormInputState.new('').map(validateNotEmpty('Required field'));

        this.form = FormModel.group({
            bettingPlatformForm: this.bettingPlatformForm,
        });
    }

    private get hasPromoCodes(): boolean {
        return this.generalDetailsState.campaignType === 'promo-code';
    }

    public validateForm(): boolean {
        if (this.hasPromoCodes === false) {
            this.form.setAsVisited();
            this.sportsbookSelectionsState.validateForm();
            this.casinoSelectionsState.validateForm();
        }
        return this.isFormValid;
    }

    private get isFormValid(): boolean {
        if (this.hasPromoCodes) {
            return true;
        }

        if (this.form.result.value.type === 'error') {
            return false;
        }

        switch (this.bettingPlatformForm.value) {
            case 'casino':
                return this.casinoSelectionsState.isFormValid;
            case 'sports-book':
                return this.sportsbookSelectionsState.isFormValid;
            default:
                return false;
        }
    }

    public get formErrorsCount(): number {
        if (this.form.errorsForView.length > 0) {
            return 1;
        }

        switch (this.bettingPlatformForm.value) {
            case 'casino':
                return this.casinoSelectionsState.formErrorsCount;
            case 'sports-book':
                return this.sportsbookSelectionsState.formErrorsCount;
            default:
                return 0;
        }
    }

    public isTouched(): boolean {
        let bettingPlatformFormIsTouched = false;

        if (this.hasPromoCodes === false) {
            switch (this.bettingPlatformForm.value) {
                case 'casino':
                    bettingPlatformFormIsTouched = this.casinoSelectionsState.isTouched();
                    break;
                case 'sports-book':
                    bettingPlatformFormIsTouched = this.sportsbookSelectionsState.isTouched();
                    break;
            }
        }

        return this.form.isTouched() || bettingPlatformFormIsTouched;
    }

    public setIsTouched(): void {
        if (this.hasPromoCodes === false) {
            switch (this.bettingPlatformForm.value) {
                case 'casino':
                    this.casinoSelectionsState.setIsTouched();
                    break;
                case 'sports-book':
                    this.sportsbookSelectionsState.setIsTouched();
                    break;
            }
        }
        this.form.setIsTouched();
    }

    public resetIsTouched(): void {
        if (this.hasPromoCodes === false) {
            switch (this.bettingPlatformForm.value) {
                case 'casino':
                    this.casinoSelectionsState.resetIsTouched();
                    break;
                case 'sports-book':
                    this.sportsbookSelectionsState.resetIsTouched();
                    break;
            }
        }
        this.form.resetIsTouched();
    }

    public resetIsVisited(): void {
        if (this.hasPromoCodes === false) {
            switch (this.bettingPlatformForm.value) {
                case 'casino':
                    this.casinoSelectionsState.resetIsVisited();
                    break;
                case 'sports-book':
                    this.sportsbookSelectionsState.resetIsVisited();
                    break;
            }
        }
        this.form.resetIsVisited();
    }

    public get actionConditions(): ActionConditionsType | undefined {
        const platformType = this.bettingPlatformForm.value;
        if (this.hasPromoCodes) {
            return undefined;
        }
        switch (platformType) {
            case 'casino':
                return {
                    bettingPlatform: 'casino',
                    criteria: this.casinoCriteria,
                    sequenceDependenceOfCriteria: [],
                };
            case 'sports-book':
                return {
                    bettingPlatform: 'sports-book',
                    criteria: this.sportsbookCriteria,
                    sequenceDependenceOfCriteria: [],
                };
            default:
                return undefined;
        }
    }

    private get sportsbookCriteria(): SportsbookCriteriaType {
        const sportsPicker = this.sportsbookSelectionsState.sportsPickerState.checked.map((e) =>
            deserializeNodeValue(e)
        );

        const sportIds = sportsPicker.filter((e) => e?.type === 'sport').map((e) => e.sportId);
        const competitionIds = sportsPicker.filter((e) => e?.type === 'competition').map((e) => e.competitionId);
        const eventIds = sportsPicker.filter((e) => e?.type === 'event').map((e) => e.eventId);
        const marketIds = sportsPicker.filter((e) => e?.type === 'market').map((e) => e.marketId);

        const criteriaMap: Map<SportsbookCriteriaKeyType, string | undefined> = new Map([
            // Betting Types & Odds Criteria
            ['bet-type', this.sportsbookSelectionsState.betTypeForm.value],
            ['bet-min-odds', this.sportsbookSelectionsState.minBetOddsForm.value],
            // Stake Criteria
            ['target-stake', this.sportsbookSelectionsState.targetStakeForm.value],
            ['bet-result', this.sportsbookSelectionsState.betResultForm.value], // only for betType: settlement
            [
                'min-stake',
                this.sportsbookSelectionsState.stakeRequirements === 'cumulative'
                    ? this.sportsbookSelectionsState.minStakeCumulativeForm.value
                    : this.sportsbookSelectionsState.minStakeSingleForm.value,
            ],
            ['max-stake', this.sportsbookSelectionsState.maxStakeSingleForm.value],
            ['min-bet-frequency', this.sportsbookSelectionsState.minBetFrequencyForm.value],
            // Eligible: Sport > Competition > Event > Market
            ['sport', JSON.stringify(sportIds)],
            ['competition', JSON.stringify(competitionIds)],
            ['event', JSON.stringify(eventIds)],
            ['market', JSON.stringify(marketIds)],
            // not in form yet, likely post-mvp
            ['selection', undefined],
        ]);

        return this.buildSportsbookCriteria(criteriaMap);
    }

    private get casinoCriteria(): CasinoCriteriaType {
        const criteriaMap: Map<CasinoCriteriaKeyType, string | undefined> = new Map([
            // Stake Criteria
            ['casino-game', JSON.stringify(this.casinoSelectionsState.gamePickerState.checked)],
            ['min-spins', this.casinoSelectionsState.numberOfSpins.value],
            ['min-monetary-value', this.casinoSelectionsState.moneteryValue.value],
            // not in form yet, likely post-mvp
            ['casino-provider', undefined],
            ['casino-result', this.casinoSelectionsState.casinoResultForm.value],
        ]);

        return this.buildCasinoCriteria(criteriaMap);
    }

    public setFormValues = async (campaignModel: CampaignModel): Promise<void> => {
        this.bettingPlatformForm.setValue(campaignModel.actionConditions?.bettingPlatform ?? '');
        await this.sportsbookSelectionsState.setFormValues(campaignModel);
        this.casinoSelectionsState.setFormValues(campaignModel);
    };

    private buildSportsbookCriteria = (
        criteriaMap: Map<SportsbookCriteriaKeyType, string | undefined>
    ): SportsbookCriteriaType => {
        const criteriaRecord: Record<
            SportsbookCriteriaKeyType,
            (value: string | undefined) => SportsbookCriteriaItemType | null
        > = {
            'min-stake': (value) => {
                const parsedValue = checkIsNumber(value);
                if (parsedValue === null) {
                    return null;
                }
                // TODO: should be changed in API to string value
                return { type: 'min-stake', value: parsedValue * 100, op: 'gte' };
            },
            'max-stake': (value) => {
                const parsedValue = checkIsNumber(value);
                if (parsedValue === null) {
                    return null;
                }
                // TODO: should be changed in API to string value
                return { type: 'max-stake', value: parsedValue * 100, op: 'lte' };
            },
            'bet-min-odds': (value) => {
                const parsedValue = checkIsNumber(value);
                if (parsedValue === null) {
                    return null;
                }
                return { type: 'bet-min-odds', value: parsedValue, op: 'gte' };
            },
            'bet-type': (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null) {
                    return null;
                }
                return { type: 'bet-type', value: parsedValue, op: 'in' };
            },
            'bet-result': (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null) {
                    return null;
                }
                return { type: 'bet-result', value: parsedValue, op: 'in' };
            },
            'target-stake': (value) => {
                const parsedValue = checkIsNumber(value);
                if (parsedValue === null) {
                    return null;
                }
                return {
                    type: 'target-stake',
                    // TODO: should be changed in API to string value
                    value: parsedValue * 100,
                    op: 'gte',
                };
            },
            'min-bet-frequency': (value) => {
                const parsedValue = checkIsNumber(value);
                if (parsedValue === null) {
                    return null;
                }
                return {
                    type: 'min-bet-frequency',
                    value: parsedValue,
                    op: 'gte',
                };
            },
            competition: (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null || parsedValue.length === 0) {
                    return null;
                }
                return { type: 'competition', value: parsedValue, op: 'in' };
            },
            sport: (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null || parsedValue.length === 0) {
                    return null;
                }
                return { type: 'sport', value: parsedValue, op: 'in' };
            },
            event: (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null || parsedValue.length === 0) {
                    return null;
                }
                return { type: 'event', value: parsedValue, op: 'in' };
            },
            market: (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null || parsedValue.length === 0) {
                    return null;
                }
                return { type: 'market', value: parsedValue, op: 'in' };
            },
            selection: () => null, // not supported for MVP
        };

        const criteria: Array<SportsbookCriteriaItemType> = [];

        for (const [key, value] of criteriaMap) {
            const getCriteriaItem = criteriaRecord[key];
            const criteriaItem = getCriteriaItem(value);

            if (criteriaItem !== null) {
                criteria.push(criteriaItem);
            }
        }

        return criteria;
    };

    private buildCasinoCriteria = (criteriaMap: Map<CasinoCriteriaKeyType, string | undefined>): CasinoCriteriaType => {
        const criteriaRecord: Record<
            CasinoCriteriaKeyType,
            (value: string | undefined) => CasinoCriteriaItemType | undefined
        > = {
            'min-spins': (value) => {
                const parsedValue = checkIsNumber(value);
                if (parsedValue === null) {
                    return undefined;
                }
                return { type: 'min-spins', value: parsedValue, op: 'gte' };
            },
            'min-monetary-value': (value) => {
                const parsedValue = checkIsNumber(value);
                if (parsedValue === null) {
                    return undefined;
                }
                return {
                    type: 'min-monetary-value',
                    value: parsedValue,
                    op: 'gte',
                };
            },
            'casino-game': (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null || parsedValue.length === 0) {
                    return undefined;
                }
                return { type: 'casino-game', value: parsedValue, op: 'in' };
            },
            'casino-provider': (value) => {
                const parsedValue = checkString(value);
                if (parsedValue === null) {
                    return undefined;
                }
                return {
                    type: 'casino-provider',
                    value: parsedValue,
                    op: 'in',
                };
            },
            'casino-result': (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null) {
                    return undefined;
                }
                return {
                    type: 'casino-result',
                    op: 'in',
                    value: parsedValue,
                };
            },
        };

        const criteria: Array<CasinoCriteriaItemType> = [];

        for (const [key, value] of criteriaMap) {
            const getCriteriaItem = criteriaRecord[key];
            const criteriaItem = getCriteriaItem(value);

            if (criteriaItem !== undefined) {
                criteria.push(criteriaItem);
            }
        }

        return criteria;
    };
}
